import cx from 'classnames'
import React, {
  AriaAttributes,
  FC,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { AriaButtonProps, useButton } from 'react-aria'
import { pxToRem } from 'src/theme'

import { Icons } from '../Icon'
import { LinkProps } from '../Link'

import * as SC from './styled'

type nav = {
  linkProps: LinkProps
  sub?: nav
}[]

export type MenuProps = AriaAttributes & {
  id?: string
  className?: string
  links?: nav
  bottomLinks?: nav
  open?: boolean
}

// this adds a stupid level of complexity and
// is only used to produce a react-aria button when there is no link on a parent item,
// sorry 💩
const LinkToggle: FC<
  AriaButtonProps & {
    label?: string
  }
> = (props) => {
  const { label, ...button } = props
  const ref = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(button, ref)

  return (
    <SC.LinkToggle ref={ref} {...buttonProps}>
      <SC.Label>{label}</SC.Label>
      <SC.Chevron icon={Icons.chevronDown} title={label} />
    </SC.LinkToggle>
  )
}

const Toggle: FC<
  AriaButtonProps & {
    label?: string
  }
> = (props) => {
  const { label, ...button } = props
  const ref = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(button, ref)

  return (
    <SC.Toggle ref={ref} {...buttonProps}>
      <SC.Chevron icon={Icons.chevronDown} title={label} />
    </SC.Toggle>
  )
}

const Menu = React.forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
  const { className, links, bottomLinks, id, open, ...aria } = props
  const idBak = useId()
  const subs = useRef<HTMLUListElement[]>([])
  const [openSubs, setOpenSubs] = useState<number[]>([])
  const toggle = useCallback(
    (index: number) => {
      if (openSubs.includes(index)) {
        setOpenSubs(openSubs.filter((i) => i !== index))
      } else {
        setOpenSubs([...openSubs, index])
      }
    },
    [openSubs]
  )

  // fetch the height of each sub to optimize
  useEffect(() => {
    const handleHeight = () => {
      subs.current.forEach((ref) => {
        if (ref) {
          const height = ref.offsetHeight
          const parent = ref.parentElement
          if (!parent) return
          const style = parent.style
          style.setProperty(`--height__sub`, `${pxToRem(height)}`)
        }
      })
    }
    // on load
    handleHeight()
    // on resize
    window.addEventListener('resize', handleHeight)
    return () => window.removeEventListener('resize', handleHeight)
  }, [subs])

  useEffect(() => {
    const body = document?.body
    if (!body) return
    if (open) {
      body.classList.add('with-menu--open')
    } else {
      body.classList.remove('with-menu--open')
      setTimeout(() => setOpenSubs([]), 300)
    }
  }, [open])

  return (
    <SC.Container
      className={cx('Menu', className)}
      id={id}
      hidden={!open}
      {...aria}
      ref={ref}
    >
      <SC.Wrapper>
        {links && (
          <SC.Section>
            <SC.List>
              {links.map(({ linkProps, sub }, index) => (
                <li key={index} data-open={openSubs.includes(index)}>
                  {sub ? (
                    !linkProps.href ? (
                      <LinkToggle
                        label={linkProps.label ?? linkProps.text}
                        aria-controls={`menu-sub-${id ?? idBak}-${index}`}
                        aria-expanded={openSubs.includes(index)}
                        onPress={() => toggle(index)}
                      />
                    ) : (
                      <SC.Item>
                        <SC._Link {...linkProps}>
                          <SC.Label>
                            {linkProps.label ?? linkProps.text}
                          </SC.Label>
                        </SC._Link>
                        <Toggle
                          aria-controls={`menu-sub-${id ?? idBak}-${index}`}
                          aria-expanded={openSubs.includes(index)}
                          onPress={() => toggle(index)}
                        />
                      </SC.Item>
                    )
                  ) : (
                    <SC._Link {...linkProps}>
                      <SC.Label>{linkProps.label ?? linkProps.text}</SC.Label>
                    </SC._Link>
                  )}
                  {sub && (
                    <SC.Sub
                      id={`menu-sub-${id ?? idBak}-${index}`}
                      hidden={!openSubs.includes(index)}
                    >
                      <ul
                        ref={(el: HTMLUListElement | null) => {
                          if (el) subs.current[index] = el
                        }}
                      >
                        {sub.map(({ linkProps }, i) => (
                          <li key={i}>
                            <SC.SubLink {...linkProps} />
                          </li>
                        ))}
                      </ul>
                    </SC.Sub>
                  )}
                </li>
              ))}
            </SC.List>
          </SC.Section>
        )}
        {bottomLinks && (
          <SC.Section>
            <SC.List>
              {bottomLinks.map(({ linkProps }, index) => (
                <li key={index}>
                  <SC._Link {...linkProps}>
                    <SC.Label>{linkProps.label ?? linkProps.text}</SC.Label>
                  </SC._Link>
                </li>
              ))}
            </SC.List>
          </SC.Section>
        )}
      </SC.Wrapper>
    </SC.Container>
  )
})

Menu.displayName = 'Menu'

export default Menu
